import { useFormikContext, getIn } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { pluck } from '../../helpers/pluck';

type TextAreaFormikProps = {
  name: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  className?: string;
  label?: string;
  required?: boolean;
  readonly?: boolean;
  value?: string;
  defaultValue?: string;
  rows?: number;
};

const TextAreaFormik: React.FC<TextAreaFormikProps> = ({
  name,
  placeholder,
  onChange,
  className,
  label,
  required,
  readonly,
  defaultValue,
  rows = 4,
}) => {
  const { values, errors, setFieldValue } = useFormikContext();
  const data = getIn(values, name);

  const [value, setValue] = useState(data || '');
  const theError = useMemo(() => pluck(name, errors) || undefined, [errors, name]);

  // Sincronizar com o valor do Formik
  useEffect(() => {
    if (data !== value) {
      setValue(data || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Aplicar defaultValue se o campo estiver vazio
  useEffect(() => {
    if (defaultValue && !data) {
      setFieldValue(name, defaultValue);
      setValue(defaultValue);
      return;
    }
    setFieldValue(name, value);
  }, [name, setFieldValue, value, defaultValue, data]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setValue(newValue);
      setFieldValue(name, newValue);
      onChange && onChange(e);
    },
    [name, setFieldValue, onChange]
  );

  return (
    <div className={className}>
      {label && (
        <label htmlFor={name} className="block font-medium text-[16px] text-gray-600">
          {label} {required ? <b className="text-redPrimary">*</b> : ''}
        </label>
      )}
      <textarea
        id={name}
        placeholder={placeholder || ''}
        onChange={handleChange}
        value={value}
        className="border border-gray text-gray2 p-2 rounded w-full bg-[#ffffff]"
        aria-required={required}
        aria-invalid={Boolean(theError)}
        aria-describedby={theError ? `${name}-error` : undefined}
        readOnly={readonly}
        rows={rows}
        style={{ resize: "none" }}
      />
      {theError ? (
        <small className="text-redPrimary/90" aria-live="polite" id={`${name}-error`}>
          {theError}
        </small>
      ) : null}
    </div>
  );
};

export default TextAreaFormik;